import { QueryErrorBoundary } from '@components/errors/QueryErrorBoundary'
import { RouteErrorFallback } from '@components/errors/RouteErrorFallback'
import { ConversationsTab } from '@features/conversations/components/ConversationsTab'
import { conversationCoursesQueryOptions, conversationTypesQueryOptions, studentConversationsQueryOptions, studentInterventionSummaryQueryOptions } from '@features/conversations/queries'
import { requireRouteAudience } from '@services/router/routeAccess'
import { createFileRoute } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'
import { LoadingIndicator } from '@ui/LoadingIndicator'
import { Suspense } from 'react'
import { Box } from '@mantine/core'
import { z } from 'zod'

const conversationsSearchSchema = z.object({
    course: z.string().optional(),
    types: z.array(z.string()).optional(),
})

export const Route = createFileRoute('/_layout/students/$studentNo/conversations')({
    component: RouteComponent,
    beforeLoad: requireRouteAudience(['staff']),
    validateSearch: zodValidator(conversationsSearchSchema),
    loader: async ({ params, context }) => {
        context.queryClient.ensureQueryData(studentConversationsQueryOptions({ studentNo: params.studentNo })),
        context.queryClient.ensureQueryData(conversationTypesQueryOptions({ studentNo: params.studentNo })),
        context.queryClient.ensureQueryData(conversationCoursesQueryOptions({ studentNo: params.studentNo })),
        context.queryClient.ensureQueryData(studentInterventionSummaryQueryOptions({ studentNo: params.studentNo }))
        return null
    },
    staticData: {
        crumb: { label: 'Conversations' }
    },
    errorComponent: ({ error, reset }) => (
        <RouteErrorFallback
            error={error}
            onRetry={reset}
            title="An error occurred"
            description="There was an error loading the conversations."
        />
    )
})

function RouteComponent() {
    const { studentNo } = Route.useParams()

    return(
        <Box className={'h-full min-h-0 w-full min-w-0'}>
            <QueryErrorBoundary title="Failed to load conversations" description="We couldn't load the conversations for this student. Please try again, and if the problem persists, contact support.">
                <Suspense fallback={<LoadingIndicator resource={"conversations"}/>}>
                    <ConversationsTab studentNo={studentNo} />
                </Suspense>
            </QueryErrorBoundary>
        </Box>
    )
}
